// Sprint 0078 — Auto-assignment rules admin page.
// Admin defines "CARDING machine in Preparatory dept → primary = Pramod
// (Carding Foreman), backup = Sanjay (Fitter)". When a breakdown is raised
// on a Carding machine and the machine has no explicit primary_technician_id,
// the system uses this rule to auto-assign.

import { useState } from 'react'
import {
  App, Button, Form, Input, Modal, Popconfirm, Select, Space, Tag, Typography,
} from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  assignmentRulesApi,
  type AssignmentRule,
} from '@/features/maintenance/assignmentRulesApi'
import { departmentsApi } from '@/features/masters/departments/api'
import { usersApi } from '@/features/users/api'
import { hrUserPickerApi } from '@/features/hr/enablementApi'
import DataTable from '@/shared/components/DataTable'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

const { Text } = Typography

// Machine types we currently support — matches the values in the machines master.
const MACHINE_TYPES = [
  'BLOW_ROOM', 'CARDING', 'COMBER', 'DRAW_FRAME_BREAKER', 'DRAW_FRAME_FINISHER',
  'LAP_FORMER', 'SIMPLEX', 'RING_FRAME', 'AUTO_CONER', 'YCP',
]

export default function AssignmentRulesPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const canEdit = usePermission('maintenance.create')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<AssignmentRule | null>(null)
  const [form] = Form.useForm()

  const { data: rules, isLoading } = useQuery({
    queryKey: ['assignment-rules'],
    queryFn: () => assignmentRulesApi.list({ include_inactive: true }),
  })

  const { data: depts } = useQuery({
    queryKey: ['departments-all'],
    queryFn: () => departmentsApi.list({ page: 1, page_size: 200 }),
  })

  // Sprint 0081 — dept-filtered picker if rule has a department set.
  // We watch the form field; if user picks a department in the modal, the
  // user dropdown narrows to that department's employees.
  const ruleDeptId = Form.useWatch('department_id', form)
  const { data: pickerUsers } = useQuery({
    queryKey: ['hr-users-picker', ruleDeptId],
    queryFn: () => hrUserPickerApi.list({
      department_id: ruleDeptId || undefined,
    }),
    staleTime: 60_000,
  })
  // Fallback to legacy usersApi when picker returns empty (e.g. no Employee
  // records linked to the chosen dept yet — let admin pick from all users).
  const { data: usersData } = useQuery({
    queryKey: ['users-fallback'],
    queryFn: () => usersApi.list({ page: 1, page_size: 500, is_active: true }),
    staleTime: 5 * 60_000,
  })

  const deptName = (id: string | null) => {
    if (!id) return 'All departments'
    return depts?.items.find((d) => d.id === id)?.name ?? '—'
  }
  const userName = (id: string | null) => {
    if (!id) return '—'
    const u = usersData?.items.find((x) => x.id === id)
    return u ? `${u.full_name ?? u.username}` : '—'
  }

  const onCreate = () => {
    setEditing(null)
    form.resetFields()
    setDrawerOpen(true)
  }
  const onEdit = (r: AssignmentRule) => {
    setEditing(r)
    form.setFieldsValue({
      machine_type: r.machine_type,
      department_id: r.department_id ?? undefined,
      primary_user_id: r.primary_user_id,
      backup_user_id: r.backup_user_id ?? undefined,
      notes: r.notes ?? '',
      is_active: r.is_active,
    })
    setDrawerOpen(true)
  }

  const saveMutation = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      if (editing) {
        return assignmentRulesApi.update(editing.id, {
          primary_user_id: values.primary_user_id as string,
          backup_user_id: (values.backup_user_id as string) || null,
          notes: (values.notes as string) || null,
          is_active: values.is_active as boolean,
        })
      }
      return assignmentRulesApi.create({
        machine_type: values.machine_type as string,
        department_id: (values.department_id as string) || null,
        primary_user_id: values.primary_user_id as string,
        backup_user_id: (values.backup_user_id as string) || null,
        notes: (values.notes as string) || null,
      })
    },
    onSuccess: () => {
      message.success(editing ? 'Rule updated' : 'Rule created')
      setDrawerOpen(false)
      qc.invalidateQueries({ queryKey: ['assignment-rules'] })
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err?.response?.data?.detail ?? 'Save failed')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => assignmentRulesApi.delete(id),
    onSuccess: () => {
      message.success('Rule deactivated')
      qc.invalidateQueries({ queryKey: ['assignment-rules'] })
    },
    onError: () => message.error('Delete failed'),
  })

  const onSubmit = () => form.validateFields().then((v) => saveMutation.mutate(v))

  const columns = [
    {
      title: 'Machine Type', dataIndex: 'machine_type', key: 'machine_type', width: 180,
      render: (v: string) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: 'Department', key: 'department_id', width: 180,
      render: (_: unknown, r: AssignmentRule) =>
        r.department_id ? deptName(r.department_id)
          : <Text type="secondary"><em>All departments</em></Text>,
    },
    {
      title: 'Primary Technician', key: 'primary_user_id',
      render: (_: unknown, r: AssignmentRule) => <Text strong>{userName(r.primary_user_id)}</Text>,
    },
    {
      title: 'Backup', key: 'backup_user_id',
      render: (_: unknown, r: AssignmentRule) => userName(r.backup_user_id),
    },
    { title: 'Notes', dataIndex: 'notes', key: 'notes', ellipsis: true },
    {
      title: 'Status', dataIndex: 'is_active', key: 'is_active', width: 90,
      render: (v: boolean) =>
        v ? <Tag color="green">ACTIVE</Tag> : <Tag>INACTIVE</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 160,
      render: (_: unknown, r: AssignmentRule) => canEdit ? (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(r)}>Edit</Button>
          <Popconfirm
            title="Deactivate this rule?"
            onConfirm={() => deleteMutation.mutate(r.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ) : null,
    },
  ]

  return (
    <div>
      <PageHeader
        title="Auto-Assignment Rules"
        subtitle="When a breakdown is raised on a machine of this type, the WO is auto-assigned to the primary technician. Backup is used when supervisor manually reassigns."
        extra={canEdit && (
          <Button type="primary" icon={<PlusOutlined />} onClick={onCreate}>
            New Rule
          </Button>
        )}
      />

      <DataTable
        rowKey="id"
        columns={columns}
        dataSource={rules ?? []}
        loading={isLoading}
      />

      <Modal
        title={editing ? `Edit rule — ${editing.machine_type}` : 'New auto-assignment rule'}
        open={drawerOpen}
        onCancel={() => setDrawerOpen(false)}
        onOk={onSubmit}
        confirmLoading={saveMutation.isPending}
        okText={editing ? 'Save' : 'Create'}
        width={520}
      >
        <Form form={form} layout="vertical" initialValues={{ is_active: true }}>
          <Form.Item
            name="machine_type"
            label="Machine Type"
            rules={[{ required: true, message: 'Pick a machine type' }]}
          >
            <Select
              disabled={!!editing}
              placeholder="Select machine type"
              options={MACHINE_TYPES.map((t) => ({ label: t, value: t }))}
            />
          </Form.Item>

          <Form.Item
            name="department_id"
            label="Department (leave blank = applies to all departments)"
          >
            <Select
              allowClear
              disabled={!!editing}
              placeholder="All departments"
              options={(depts?.items ?? []).map((d) => ({
                label: d.name, value: d.id,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="primary_user_id"
            label="Primary Technician (auto-assigned on breakdown)"
            rules={[{ required: true, message: 'Pick a primary technician' }]}
          >
            {/* Sprint 0081 — prefer dept-linked employees, fall back to all
                users if the dept has no Employee records yet. */}
            <Select
              showSearch
              placeholder={
                ruleDeptId
                  ? "Pick from this department's employees"
                  : "Pick from all linked employees"
              }
              options={
                (pickerUsers && pickerUsers.length > 0
                  ? pickerUsers.map((u) => ({
                      label: `${u.full_name} (${u.username})`
                        + (u.designation ? ` — ${u.designation}` : ''),
                      value: u.id,
                    }))
                  : (usersData?.items ?? []).map((u) => ({
                      label: `${u.full_name ?? u.username} (${u.username})`,
                      value: u.id,
                    }))
                )
              }
              filterOption={(input, opt) =>
                ((opt?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            name="backup_user_id"
            label="Backup Technician (used when supervisor reassigns)"
          >
            <Select
              allowClear
              showSearch
              placeholder="Select backup user"
              options={
                (pickerUsers && pickerUsers.length > 0
                  ? pickerUsers.map((u) => ({
                      label: `${u.full_name} (${u.username})`
                        + (u.designation ? ` — ${u.designation}` : ''),
                      value: u.id,
                    }))
                  : (usersData?.items ?? []).map((u) => ({
                      label: `${u.full_name ?? u.username} (${u.username})`,
                      value: u.id,
                    }))
                )
              }
              filterOption={(input, opt) =>
                ((opt?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notes (optional)"
            tooltip="E.g. 'Pramod Goswami is Carding Foreman, day shift only'"
          >
            <Input.TextArea rows={2} />
          </Form.Item>

          {editing && (
            <Form.Item name="is_active" label="Active" valuePropName="checked">
              <Select
                options={[
                  { label: 'Active', value: true },
                  { label: 'Inactive', value: false },
                ]}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  )
}
